import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home, Compass, Users, Star, Upload, Crown, Megaphone,
  Settings, LogOut, LogIn, UserCircle, Globe2, Menu, X, Flame, Calendar, Library, Info, Briefcase, MessageCircle, Handshake, MapPin, MessagesSquare, Coins, Heart, Film, Sparkles, Trophy,
} from "lucide-react";
import { MadafricaLogo } from "./MadafricaLogo";
import { NotificationBell } from "./NotificationBell";
import { SidebarSearch } from "./SidebarSearch";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useI18n } from "../i18n/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const NAV_GROUPS = [
  {
    titleKey: "nav.menu",
    title: "Menu",
    items: [
      { to: "/", label: "Accueil", i18nKey: "nav.home", icon: Home, key: "home" },
      { to: "/explore", label: "Explorer", i18nKey: "nav.explore", icon: Compass, key: "explore" },
      { to: "/artists", label: "Artistes", i18nKey: "nav.artists", icon: Users, key: "artists" },
      { to: "/events", label: "Évènements", i18nKey: "nav.events", icon: Calendar, key: "events" },
    ],
  },
  {
    titleKey: "nav.discover",
    title: "Découverte",
    items: [
      { to: "/trending", label: "Top pays", i18nKey: "nav.top_countries", icon: Globe2, key: "trending" },
      { to: "/shorts", label: "Shorts 📱", icon: Film, key: "shorts", ignoreToggle: true },
      { to: "/carte", label: "Carte 🇲🇬", i18nKey: "nav.map", icon: MapPin, key: "map", ignoreToggle: true },
      { to: "/chat", label: "Chat général", i18nKey: "nav.chat", icon: MessagesSquare, key: "chat", ignoreToggle: true },
      { to: "/rising", label: "Stars montantes", i18nKey: "nav.rising", icon: Flame, key: "rising" },
      { to: "/topmada", label: "Top Mada 🏆", icon: Trophy, key: "topmada", ignoreToggle: true },
      { to: "/studio-ia-local", label: "Studio IA Local 🧬", icon: Sparkles, key: "studio_ia_local", ignoreToggle: true },
      { to: "/featured", label: "En vedette", i18nKey: "nav.featured", icon: Star, key: "featured" },
      { to: "/vola", label: "Gagner des Vola 💰", i18nKey: "nav.vola", icon: Coins, key: "vola" },
      { to: "/sondages", label: "Sondages 📋", icon: MessagesSquare, key: "surveys" },
      { to: "/rencontres", label: "Rencontres ❤️", icon: Heart, key: "dating" },
    ],
  },
  {
    titleKey: "footer.more",
    title: "Plus",
    items: [
      { to: "/castings", label: "Appel à candidatures", i18nKey: "footer.casting", icon: Briefcase, key: "castings" },
      { to: "/about", label: "À propos", i18nKey: "footer.about", icon: Info, key: "about", ignoreToggle: true },
      { to: "/partners", label: "Partenaires", i18nKey: "footer.partners", icon: Handshake, key: "partners", ignoreToggle: true },
    ],
  },
];

const NAV = NAV_GROUPS.flatMap((g) => g.items); // rétrocompat pour mobile drawer

const ACTIONS = [
  { to: "/library", label: "Ma bibliothèque", i18nKey: "nav.library", icon: Library, key: "library", auth: true },
  { to: "/messages", label: "Messages", i18nKey: "nav.notifications", icon: MessageCircle, key: "messages", auth: true, ignoreToggle: true },
  { to: "/upload", label: "Uploader", i18nKey: "nav.upload", icon: Upload, key: "upload", auth: true },
  { to: "/dashboard", label: "Dashboard", i18nKey: "nav.studio", icon: Settings, key: "dashboard", auth: true, ignoreToggle: true },
  { to: "/premium", label: "Premium", i18nKey: "footer.premium", icon: Crown, key: "premium" },
  { to: "/annoncer", label: "Annoncer chez nous", icon: Megaphone, key: "advertisers", ignoreToggle: true },
];

export const MobileHeader = ({ enabledSections = {}, onMenuClick }) => {
  const navigate = useNavigate();
  return (
    <header
      className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 backdrop-blur-xl bg-[#0F0C0B]/80 border-b border-white/5"
      data-testid="mobile-header"
    >
      <button onClick={() => navigate("/")} data-testid="mobile-logo-btn">
        <MadafricaLogo size={90} withText />
      </button>
      <div className="flex items-center gap-1">
        <NotificationBell />
        <button
          onClick={onMenuClick}
          className="p-2 rounded-xl hover:bg-white/5 text-white/80 hover:text-white transition-colors"
          data-testid="mobile-menu-btn"
          aria-label="Ouvrir le menu"
        >
          <Menu size={24} />
        </button>
      </div>
    </header>
  );
};

export const MobileDrawer = ({ open, onClose, enabledSections = {} }) => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const isEnabled = (key) => enabledSections[key] !== false;
  const lbl = (item) => item.i18nKey ? t(item.i18nKey) : item.label;

  const handleLogout = async () => {
    await logout();
    onClose();
    navigate("/");
  };

  const itemCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
      isActive ? "bg-brand-red/15 text-brand-red" : "text-white/80 hover:bg-white/5"
    }`;

  return (
    <>
      {open && <div className="fixed inset-0 z-50 bg-black/60 lg:hidden" onClick={onClose} data-testid="mobile-drawer-backdrop" />}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-50 w-80 max-w-[85vw] bg-[#0F0C0B] border-l border-white/10 flex flex-col transform transition-transform duration-300 lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="mobile-drawer"
      >
        <div className="flex items-center justify-between px-5 pt-6 pb-4 shrink-0">
          <MadafricaLogo size={80} withText />
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/5" data-testid="mobile-drawer-close">
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="px-5 pb-3 shrink-0">
          <SidebarSearch onNavigate={onClose} />
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 sidebar-scroll">
          <nav className="flex flex-col gap-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mb-2 px-2">{t("nav.discover")}</p>
            {NAV.filter((n) => n.ignoreToggle || isEnabled(n.key)).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={itemCls} onClick={onClose} data-testid={`mobile-nav-${n.key}`}>
                <n.icon size={18} /> <span className="text-sm font-medium">{lbl(n)}</span>
              </NavLink>
            ))}

            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold mt-6 mb-2 px-2">{t("nav.studio")}</p>
            {ACTIONS.filter((a) => a.ignoreToggle || isEnabled(a.key)).map((a) => {
              if (a.auth && !user) return null;
              return (
                <NavLink key={a.to} to={a.to} className={itemCls} onClick={onClose} data-testid={`mobile-nav-${a.key}`}>
                  <a.icon size={18} /> <span className="text-sm font-medium">{lbl(a)}</span>
                </NavLink>
              );
            })}
            {user?.is_admin && (
              <NavLink to="/admin" className={itemCls} onClick={onClose} data-testid="mobile-nav-admin">
                <Settings size={18} /> <span className="text-sm font-medium">{t("nav.admin")}</span>
              </NavLink>
            )}
            {user?.is_arnak_admin && !user?.is_admin && (
              <NavLink to="/admin" className={itemCls} onClick={onClose} data-testid="mobile-nav-arnak-admin">
                <Settings size={18} /> <span className="text-sm font-medium">Panel ARNAK</span>
              </NavLink>
            )}
          </nav>
        </div>

        <div className="px-5 pt-4 pb-6 border-t border-white/10 shrink-0">
          {user ? (
            <div className="flex flex-col gap-2">
              <NavLink to={`/artist/${user.user_id}`} onClick={onClose} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-yellow flex items-center justify-center text-white font-bold text-sm">
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{user.name}</p>
                  <p className="text-[11px] text-white/50">{user.is_premium ? "Premium" : "Gratuit"}</p>
                </div>
              </NavLink>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm text-white/60 hover:text-white rounded-xl hover:bg-white/5" data-testid="mobile-logout-btn">
                <LogOut size={16} /> {t("auth.logout")}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <button onClick={() => { onClose(); navigate("/login"); }} className="btn-primary text-sm" data-testid="mobile-login-btn">
                <LogIn size={16} className="inline mr-2" /> {t("auth.login")}
              </button>
              <button onClick={() => { onClose(); navigate("/register"); }} className="btn-ghost text-sm" data-testid="mobile-register-btn">
                <UserCircle size={16} className="inline mr-2" /> {t("auth.register")}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export const Sidebar = ({ enabledSections = {} }) => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [live, setLive] = useState({ online: 0, members: 0 });
  const lbl = (item) => item.i18nKey ? t(item.i18nKey) : item.label;

  useEffect(() => {
    const fetchLive = () => api.get("/stats/live").then(({ data }) => setLive(data)).catch(() => {});
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!user) { setUnreadMsg(0); return; }
    let mounted = true;
    const fetchUnread = () => api.get("/messages/unread-count")
      .then(({ data }) => mounted && setUnreadMsg(data.count || 0))
      .catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, [user]);

  const isEnabled = (key) => enabledSections[key] !== false;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItemCls = ({ isActive }) =>
    `group relative flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
      isActive
        ? "bg-gradient-to-r from-brand-red/15 via-brand-red/5 to-transparent text-white font-semibold"
        : "text-white/65 hover:bg-white/5 hover:text-white"
    }`;

  const activeIndicator = (isActive) =>
    isActive ? (
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-gradient-to-b from-brand-red to-brand-orange" />
    ) : null;

  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 bg-[#0F0C0B] border-r border-white/5">
      {/* Haut fixe : logo + notif */}
      <div className="px-5 pt-6 pb-2 shrink-0">
        <button
          onClick={() => navigate("/")}
          className="text-left mb-4 flex items-center justify-between w-full"
          data-testid="logo-home-btn"
        >
          <MadafricaLogo size={80} withText />
        </button>
        <div className="flex items-center gap-2 justify-end mb-3">
          <LanguageSwitcher />
          <NotificationBell />
        </div>
        <SidebarSearch />
      </div>

      {/* Milieu scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 sidebar-scroll">
        <nav className="flex flex-col gap-0.5" data-testid="sidebar-nav">
          {NAV_GROUPS.map((group, gi) => {
            const visible = group.items.filter((n) => n.ignoreToggle || isEnabled(n.key));
            if (!visible.length) return null;
            return (
              <div key={group.title} className={gi > 0 ? "mt-5" : ""}>
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 font-bold mb-1.5 px-4">
                  {group.titleKey ? t(group.titleKey) : group.title}
                </p>
                {visible.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    className={navItemCls}
                    data-testid={`nav-${n.key}`}
                  >
                    {({ isActive }) => (
                      <>
                        {activeIndicator(isActive)}
                        <n.icon size={17} className={isActive ? "text-brand-red" : ""} />
                        <span className="text-sm">{lbl(n)}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}

          <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 font-bold mt-6 mb-1.5 px-4">
            {t("nav.studio")}
          </p>
          {ACTIONS.filter((a) => a.ignoreToggle || isEnabled(a.key)).map((a) => {
            if (a.auth && !user) return null;
            return (
              <NavLink key={a.to} to={a.to} className={navItemCls} data-testid={`nav-${a.key}`}>
                {({ isActive }) => (
                  <>
                    {activeIndicator(isActive)}
                    <a.icon size={17} className={isActive ? "text-brand-red" : ""} />
                    <span className="text-sm">{lbl(a)}</span>
                    {a.key === "messages" && unreadMsg > 0 && (
                      <span className="ml-auto text-[10px] bg-brand-red text-white rounded-full px-2 py-0.5 font-bold" data-testid="sidebar-unread-badge">
                        {unreadMsg}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}

          {user?.is_admin && (
            <NavLink to="/admin" className={navItemCls} data-testid="nav-admin">
              {({ isActive }) => (
                <>
                  {activeIndicator(isActive)}
                  <Settings size={17} className={isActive ? "text-brand-red" : ""} />
                  <span className="text-sm">{t("nav.admin")}</span>
                </>
              )}
            </NavLink>
          )}
          {user?.is_arnak_admin && !user?.is_admin && (
            <NavLink to="/admin" className={navItemCls} data-testid="nav-arnak-admin">
              {({ isActive }) => (
                <>
                  {activeIndicator(isActive)}
                  <Settings size={17} className={isActive ? "text-brand-red" : ""} />
                  <span className="text-sm">Panel ARNAK</span>
                </>
              )}
            </NavLink>
          )}
        </nav>
      </div>

      {/* Bas fixe : profil / auth */}
      <div className="px-5 pt-4 pb-6 border-t border-white/5 shrink-0">
        {/* Live pills */}
        <div className="flex items-center gap-2 mb-3 text-[11px]" data-testid="sidebar-live">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#0B8B47]/15 border border-[#0B8B47]/30 text-[#12A859] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-[#12A859] animate-pulse" />
            <span>{live.online}</span>
            <span className="text-white/40">en ligne</span>
          </span>
          <span className="inline-flex items-center gap-1 text-white/50">
            · <span className="font-semibold text-white/80">{live.members}</span> membres
          </span>
        </div>
        {user ? (
          <div className="flex flex-col gap-2">
            <NavLink
              to={`/artist/${user.user_id}`}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5"
              data-testid="sidebar-profile-link"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-yellow flex items-center justify-center text-white font-bold text-sm shrink-0">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{user.name}</p>
                <p className="text-[11px] text-white/50 truncate">
                  {user.is_premium ? "Premium" : "Gratuit"}
                </p>
              </div>
            </NavLink>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white/60 hover:text-white rounded-xl hover:bg-white/5"
              data-testid="sidebar-logout-btn"
            >
              <LogOut size={16} /> {t("auth.logout")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate("/login")}
              className="btn-primary text-sm"
              data-testid="sidebar-login-btn"
            >
              <LogIn size={16} className="inline mr-2" /> {t("auth.login")}
            </button>
            <button
              onClick={() => navigate("/register")}
              className="btn-ghost text-sm"
              data-testid="sidebar-register-btn"
            >
              <UserCircle size={16} className="inline mr-2" /> {t("auth.register")}
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
